<template>
  <Card>
    <template #content>
      <h2>{{ 
        props.selectedSite 
          ? `Active Processing for ${props.selectedSite.name}` 
          : 'Active processing'
      }}</h2>
      <p>{{ 
        props.siteIdFilter || props.selectedSite 
          ? 'View the processing campaigns that are currently active for this site' 
          : 'View all active processing campaigns across all sites'
      }}</p>
      <DataTable 
        :value="activeProcesses" 
        :paginator="true" 
        :rows="10"
        :key="sortKey"
      >
        <Column field="name" header="Process">
          <template #body="slotProps">
            <router-link 
              :to="{ name: 'ProjectDetail', params: { id: slotProps.data.id } }"
              class="project-link"
              v-tooltip.top="slotProps.data.description || 'No description available'"
            >
              {{ slotProps.data.name }}
            </router-link>
          </template>
        </Column>
        <Column field="status" header="Status"></Column>
        <Column 
          v-if="!props.selectedSite && !props.siteIdFilter" 
          field="area_of_interest_name" 
          header="Site"
        ></Column>
        <Column header="Active" :style="{ width: '100px' }">
          <template #body="slotProps">
            <div :class="{ 'opacity-50': slotProps.data.author !== currentUserId }">
              <Checkbox
                :model-value="getEffectiveCheckboxState(slotProps.data)"
                :disabled="slotProps.data.author !== currentUserId"
                @click="handleCheckboxClick(slotProps.data, $event)"
                binary
              />
            </div>
          </template>
        </Column>
        <Column field="parameters" header="Configuration">
          <template #body="slotProps">
            <ul>
              <li v-if="slotProps.data.parameters.start_date">
                Start Date: {{ slotProps.data.parameters.start_date }}
              </li>
              <li v-if="slotProps.data.parameters.end_date">
                End Date: {{ slotProps.data.parameters.end_date }}
              </li>
              <li v-for="(paramVal, paramKey) in slotProps.data.parameters" :key="String(paramKey)">
                <p v-if="String(paramKey) !== 'start_date' && String(paramKey) !== 'end_date'">
                  {{ formatKey(String(paramKey)) }}: {{ formatValue(paramVal) }}
                </p>
              </li>
            </ul>
          </template>
        </Column>
      </DataTable>
    </template>
  </Card>

  <!-- Deactivation Confirmation Dialog -->
  <Dialog 
    v-model:visible="showDeactivateDialog" 
    modal 
    header="Confirm Project Deactivation" 
    :style="{ width: '450px' }"
  >
    <div class="mb-4">
      <p class="mb-3">
        <strong>Warning:</strong> Deactivating a job before it has been fully processed may lose progress due to input data cleanup services. 
        This is only an issue if your project is actively being processed.
      </p>
      <p v-if="pendingDeactivateProject">
        Are you sure you want to deactivate "<strong>{{ pendingDeactivateProject.name }}</strong>"?
      </p>
    </div>
    <div class="flex justify-end gap-2">
      <Button 
        type="button" 
        label="Cancel" 
        severity="secondary" 
        @click="cancelDeactivate"
      />
      <Button 
        type="button" 
        label="Deactivate" 
        severity="danger"
        @click="confirmDeactivate"
      />
    </div>
  </Dialog>
</template>

<script setup lang="ts">
import { ref, onMounted, defineProps, computed } from 'vue'
import { Card, DataTable, Column, Checkbox, Dialog, Button } from 'primevue'
import type { Site } from '@/composables/useSites'
import { useAuthStore } from '@/stores/auth'

export interface ActiveProcessingProps {
  selectedSite?: Site | null
  siteIdFilter?: number | number[] | null | undefined
}

const props = defineProps<ActiveProcessingProps>()
const authStore = useAuthStore()

const allActiveProcesses = ref<any[]>([])
const currentUserId = ref<number | null>(null)
const showDeactivateDialog = ref(false)
const pendingDeactivateProject = ref<any>(null)
const pendingToggleStates = ref<Map<number, boolean>>(new Map())

// Get current user ID
onMounted(async () => {
  currentUserId.value = await authStore.get_pk()
  await fetchActiveProcesses()
})

// Computed property to filter processes based on siteIdFilter or selectedSite
const activeProcesses = computed(() => {
  let filterIds: number[] = []
  
  // Determine what site IDs to filter by
  if (props.siteIdFilter) {
    if (Array.isArray(props.siteIdFilter)) {
      if (props.siteIdFilter.length === 0) {
        return sortProcesses(allActiveProcesses.value) // Empty array means no filter
      }
      filterIds = props.siteIdFilter
    } else {
      filterIds = [props.siteIdFilter] // Single ID
    }
  } else if (props.selectedSite?.id) {
    // Backward compatibility with selectedSite
    filterIds = [props.selectedSite.id]
  }
  
  // If no filter criteria, return all processes (sorted)
  if (filterIds.length === 0) {
    return sortProcesses(allActiveProcesses.value)
  }
  
  // Filter by area_of_interest matching the site IDs, then sort
  const filtered = allActiveProcesses.value.filter(process => 
    filterIds.includes(process.area_of_interest)
  )
  return sortProcesses(filtered)
})

// Computed property to create unique sort key for forcing DOM updates
const sortKey = computed(() => {
  // Create a unique key based on the sorted order of process IDs and priorities
  const processes = activeProcesses.value
  return processes.map(p => `${p.id}-${p.priority}`).join(',')
})

// Get effective checkbox state (considering pending toggles)
const getEffectiveCheckboxState = (project: any) => {
  const pendingState = pendingToggleStates.value.get(project.id)
  return pendingState !== undefined ? pendingState : (project.priority === 1)
}

// Sort processes: active first (priority === 1), then by date created (oldest first)
const sortProcesses = (processes: any[]) => {
  return [...processes].sort((a, b) => {
    // First sort by active status (priority === 1 comes first)
    const aActive = a.priority === 1 ? 1 : 0
    const bActive = b.priority === 1 ? 1 : 0
    
    if (aActive !== bActive) {
      return bActive - aActive // Active (1) before inactive (0)
    }
    
    // If both have same active status, sort by created date (oldest first)
    const aDate = new Date(a.created || a.date_created || 0)
    const bDate = new Date(b.created || b.date_created || 0)
    return aDate.getTime() - bDate.getTime() // Oldest first
  })
}

// Format keys for better display (e.g., "start_date" → "Start Date")
const formatKey = (key: string) => key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())

// Format values (e.g., boolean to "Yes/No")
const formatValue = (value: any) => (typeof value === "boolean" ? (value ? "Yes" : "No") : value)

// Handle checkbox click (prevent default and manage state manually)
const handleCheckboxClick = async (project: any, event: Event) => {
  // Prevent the default checkbox behavior
  event.preventDefault()
  event.stopPropagation()
  
  const currentState = getEffectiveCheckboxState(project)
  const newState = !currentState
  
  if (!newState) {
    // User is trying to deactivate - show confirmation dialog without changing visual state
    pendingDeactivateProject.value = project
    showDeactivateDialog.value = true
    return
  }

  // User is activating - proceed directly
  pendingToggleStates.value.delete(project.id) // Clear any pending state
  await updateProjectPriority(project, 1)
}

// Toggle project priority (active/inactive) - kept for backwards compatibility
const toggleProjectPriority = async (project: any, isActive: boolean) => {
  // This function is now mainly used by the confirmation dialog
  if (!isActive) {
    await updateProjectPriority(project, 0)
  } else {
    await updateProjectPriority(project, 1)
  }
}

// Cancel deactivation
const cancelDeactivate = () => {
  showDeactivateDialog.value = false
  // Clear the pending state to restore original checkbox state
  if (pendingDeactivateProject.value) {
    pendingToggleStates.value.delete(pendingDeactivateProject.value.id)
  }
  pendingDeactivateProject.value = null
}

// Confirm deactivation
const confirmDeactivate = async () => {
  if (pendingDeactivateProject.value) {
    // Clear pending state and proceed with deactivation
    pendingToggleStates.value.delete(pendingDeactivateProject.value.id)
    await toggleProjectPriority(pendingDeactivateProject.value, false)
  }
  showDeactivateDialog.value = false
  pendingDeactivateProject.value = null
}

// Update project priority (extracted logic)
const updateProjectPriority = async (project: any, newPriority: number) => {
  try {
    const token = await authStore.getCsrfToken()
    
    // Determine appropriate status based on priority change
    let newStatus = project.status
    if (newPriority === 1) {
      // Activating - set to a queueable status so Celery worker can assign queue position
      newStatus = 'Awaiting Prioritisation'
    } else if (newPriority === 0) {
      // Deactivating - set to deactivated status
      newStatus = 'Temporarily Deactivated'
    }
    
    const response = await fetch(`/api/projects/${project.id}/`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRFToken': token,
      },
      body: JSON.stringify({
        priority: newPriority,
        status: newStatus
      }),
    })

    if (!response.ok) {
      const errorText = await response.text()
      console.error('Failed to update project priority:', errorText)
      throw new Error(`Failed to update project priority: ${errorText}`)
    }

    // Update the local data
    const projectIndex = allActiveProcesses.value.findIndex(p => p.id === project.id)
    if (projectIndex !== -1) {
      allActiveProcesses.value[projectIndex].priority = newPriority
      allActiveProcesses.value[projectIndex].status = newStatus
    }

    console.log(`Project ${project.name} priority updated to ${newPriority}, status updated to ${newStatus}`)
    
  } catch (error: any) {
    console.error('Error updating project priority:', error?.message || error)
    // Optionally show a toast notification here
  }
}

const fetchActiveProcesses = async () => {
  console.log('Fetch active processes is TRIGGERED')
  
  try {
    // Always fetch all projects and filter reactively
    const response = await fetch('/api/projects/')
    if (!response.ok) {
      const errorText = await response.text() // Get detailed error message
      console.error('Failed to fetch active processes:', errorText)
      throw new Error(`Failed to fetch active processes: ${errorText}`)
    }

    const data = await response.json()
    console.log('Active processes:', data)
    allActiveProcesses.value = data // Store all processes
    
  } catch (error: any) {
    console.error('Error fetching active processes:', error?.message || error)
  }
}

// Expose the fetch function so parent can refresh the data
defineExpose({
  refreshActiveProcesses: fetchActiveProcesses
})

onMounted(async () => {
  // Always fetch all processes and filter reactively
  await fetchActiveProcesses()
})
</script>

<style scoped>
/* Add any component-specific styles here */
.process-name-with-tooltip {
  cursor: help;
  border-bottom: 1px dotted #999;
  text-decoration: none;
}

.process-name-with-tooltip:hover {
  border-bottom-color: var(--primary-color, #3b82f6);
}

.project-link {
  color: var(--primary-color, #3b82f6);
  text-decoration: none;
  border-bottom: 1px dotted var(--primary-color, #3b82f6);
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s ease;
}

.project-link:hover {
  color: var(--primary-color-dark, #2563eb);
  border-bottom-style: solid;
  border-bottom-width: 2px;
}
</style>
